/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { cx, jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import VideoPauseOverlayIcon from '@atlaskit/icon/core/video-pause-overlay';
import VideoPlayOverlayIcon from '@atlaskit/icon/core/video-play-overlay';
import { Pressable } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { useAudioPlayerStreamingContext } from '../../../context/AudioPlayerStreamingProvider';
import { useAudioPlayerStore } from '../../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreamingStore } from '../../../hooks/useAudioPlayerStreamingStore';
import { useAudioPlayPauseShortcut } from '../../../hooks/useAudioPlayPauseShortcut';
import { AudioStreamingState } from '../../../state/AudioPlayerStreamingStore';

const i18n = defineMessages({
	pauseButtonLabel: {
		id: 'audio.play-pause-button.pause-label',
		defaultMessage: 'Pause',
		description:
			'The label is used as a tooltip and for accessibility for the icon button which will pause the actively playing audio',
	},
	playButtonLabel: {
		id: 'audio.play-pause-button.play-label',
		defaultMessage: 'Play',
		description:
			'The label is used as a tooltip and for accessibility for the icon button which will play the actively paused audio',
	},
	loadingButtonLabel: {
		id: 'audio.play-pause-button.loading-label',
		defaultMessage: 'Loading',
		description:
			'The label is used as a tooltip and for accessibility for the icon button when the audio is loading',
	},
	loadingAnnouncement: {
		id: 'audio.play-pause-button.loading-announcement',
		defaultMessage: 'Loading audio, please wait.',
		description:
			'Announcement for screen readers when the audio player enters loading state after play is pressed',
	},
});

const styles = cssMap({
	button: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '64px',
		height: '64px',
		borderRadius: token('radius.full'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		backgroundColor: token('color.background.neutral'),
	},
	hover: {
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.pressed'),
		},
	},
	icon: {
		display: 'inline-block',
		transform: 'scale(1.25)',
	},
});

export type PlayPauseButtonProps = {
	onClick?: () => void;
};

export const PlayPauseButton = (): React.ReactNode => {
	const { formatMessage } = useIntl();
	const buttonRef = useRef<HTMLButtonElement>(null);
	const shouldRestoreFocusRef = useRef(false);

	const { state: streamingState } = useAudioPlayerStreamingStore();
	const { isPlaying, pendingContent, activatePendingContent, activeContent } = useAudioPlayerStore();
	const { fetchAudio, audioRef } = useAudioPlayerStreamingContext();
	const { trackAIResultAction } = useAIEventsInstrumentation();

	const isLoading = streamingState.state === AudioStreamingState.LOADING;

	// Restore focus after state updates complete (when isPlaying or isLoading changes)
	useEffect(() => {
		if (shouldRestoreFocusRef.current && buttonRef.current && !isLoading) {
			requestAnimationFrame(() => {
				if (buttonRef.current && !isLoading) {
					buttonRef.current.focus();
				}
				shouldRestoreFocusRef.current = false;
			});
		}
	}, [isPlaying, isLoading]);

	const { buttonContent, testId } = useMemo(() => {
		let buttonContent: React.ReactNode;
		let testId = 'miniplayer-play';

		if (isLoading) {
			testId = 'miniplayer-loading';
			buttonContent = <Spinner label={formatMessage(i18n.loadingButtonLabel)} />;
		} else if (isPlaying && !pendingContent) {
			buttonContent = <VideoPauseOverlayIcon label={formatMessage(i18n.pauseButtonLabel)} />;
			testId = 'miniplayer-pause';
		} else {
			buttonContent = <VideoPlayOverlayIcon label={formatMessage(i18n.playButtonLabel)} />;
		}

		return { buttonContent, testId };
	}, [isLoading, isPlaying, pendingContent, formatMessage]);

	const handlePause = useCallback(() => {
		if (audioRef.current) {
			audioRef.current.pause();
		}
	}, [audioRef]);

	const handlePlay = useCallback(async () => {
		if (audioRef.current) {
			await audioRef.current.play();
		}
	}, [audioRef]);

	const handleClick = useCallback(async () => {
		// Mark that we should restore focus after state updates
		shouldRestoreFocusRef.current = true;

		if (isPlaying && !pendingContent) {
			trackAIResultAction('audioPause', {
				attributes: {
					source: 'expanded-audio-player',
				},
			});
			handlePause();
		} else if (streamingState.state === AudioStreamingState.UNINITIALIZED || pendingContent) {
			trackAIResultAction('audioPlay', {
				attributes: {
					source: 'expanded-audio-player',
					action: 'fetch',
				},
			});
			if (pendingContent) {
				handlePause();
				activatePendingContent();
			}
			fetchAudio();
		} else {
			trackAIResultAction('audioPlay', {
				attributes: {
					source: 'expanded-audio-player',
					action: 'resume',
				},
			});
			await handlePlay();
		}
	}, [isPlaying, pendingContent, streamingState.state, handlePause, handlePlay, activatePendingContent, fetchAudio, trackAIResultAction]);

	// Global keyboard shortcut: Alt/Option + P to toggle play/pause
	useAudioPlayPauseShortcut(handleClick);

	return (
		<Pressable
			ref={buttonRef}
			onClick={handleClick}
			xcss={cx(styles.button, !isLoading && styles.hover)}
			testId={testId}
			isDisabled={isLoading || !activeContent?.contentId}
		>
			<span css={styles.icon}>{buttonContent}</span>
		</Pressable>
	);
};
